import { useCallback, useState } from 'react';
import type { DeepReadonly } from '@itandi/itandi-bb-ui';
import {
  Td,
  Tr,
  Dropdown,
  DropdownMenu,
  Grid,
  Box,
  Chip,
  colors,
  Modalv2,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Button,
  Alert
} from '@itandi/itandi-bb-ui';
import { toast } from 'react-toastify';
import type {
  LineUserAccount,
  LineUserAccountEdit
} from '~/models/LineUserAccount';
import { LineUserAccountModal } from '~/components/common/line_user_accounts/LineUserAccountModal';
import type { NskResponse } from '~/models/Api';
import { isSuccess } from '~/utils/api/api_handler';
import { renderDropDownButton } from '~/components/common/parts/dropdown/DropDownButton';

type Props = DeepReadonly<{
  lineUserAccount: LineUserAccount;
  handleMutate: () => Promise<void>;
  handleUpdate: (
    lineUserAccountEdit: LineUserAccountEdit,
    lineUserAccountId: number
  ) => Promise<NskResponse<void>>;
  handleDestroy: (lineUserAccountId: number) => Promise<NskResponse<void>>;
}>;

export const LineUserAccountListRow: React.FC<Props> = ({
  lineUserAccount,
  handleMutate,
  handleUpdate,
  handleDestroy
}: Props) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const handleDestroySubmit = useCallback(async () => {
    const result = await handleDestroy(lineUserAccount.id);
    if (isSuccess(result)) {
      toast.success('削除しました');
      await handleMutate();
      setIsDeleteModalOpen(false);
      return;
    }
    toast.error(`エラーが発生しました。${result.message}`);
  }, [handleDestroy, handleMutate, lineUserAccount.id]);

  return (
    <>
      <Tr>
        <Td>
          <Grid autoFlow="column" justifyContent="space-between">
            <Box>{lineUserAccount.name}</Box>
            {lineUserAccount.registered && (
              <Box marginRight={10}>
                <Chip
                  background={colors['gray-700']}
                  label="通知設定登録済み"
                  textColor={colors['itandi-white']}
                />
              </Box>
            )}
          </Grid>
        </Td>
        <Td>
          <Dropdown button={renderDropDownButton('操作')}>
            <DropdownMenu onClick={() => setIsEditModalOpen(true)}>
              編集
            </DropdownMenu>
            <DropdownMenu
              danger
              disabled={lineUserAccount.registered}
              onClick={() => setIsDeleteModalOpen(true)}
            >
              削除
            </DropdownMenu>
          </Dropdown>
        </Td>
      </Tr>
      <LineUserAccountModal
        handleClose={() => setIsEditModalOpen(false)}
        handleMutate={handleMutate}
        handleUpdate={handleUpdate}
        isOpen={isEditModalOpen}
        lineUserAccount={lineUserAccount}
      />
      <Modalv2
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        size="s"
      >
        <ModalHeader title="通知設定の削除" />
        <ModalBody>
          「{lineUserAccount.name}」を削除します。
          <Alert mt={16} variant="warning">
            この操作は元に戻せません。
          </Alert>
        </ModalBody>
        <ModalFooter
          leftArea={
            <Button
              onClick={() => {
                setIsDeleteModalOpen(false);
              }}
              variant="secondary"
            >
              キャンセル
            </Button>
          }
          rightArea={
            <Button onClick={handleDestroySubmit} variant="danger">
              削除
            </Button>
          }
        />
      </Modalv2>
    </>
  );
};
